import { useEffect, useState } from "react";

import { ActionIcon, Alert, Box, Group, Stack, Text } from "@mantine/core";

import { IconArrowDown, IconArrowUp, IconHelpCircle } from "@tabler/icons-react";

import { TextWrapper } from "@/components/TextWrapper";

interface InsuranceItem {
  id: string;
  name: string;
  rank: number;
}

interface InsuranceOrderableListProps {
  insurances: InsuranceItem[];
  onChange?: (orderedInsurances: InsuranceItem[]) => void;
}

export default function InsuranceOrderableList({
  insurances,
  onChange,
}: InsuranceOrderableListProps) {
  const [orderedItems, setOrderedItems] = useState<InsuranceItem[]>([]);
  const [isAlertVisible, setIsAlertVisible] = useState(true);

  useEffect(() => {
    setOrderedItems(insurances);
  }, [insurances]);

  const handleMoveUp = (index: number) => {
    if (index === 0) return;

    const newItems = [...orderedItems];
    [newItems[index - 1], newItems[index]] = [newItems[index], newItems[index - 1]];

    newItems.forEach((item, idx) => {
      item.rank = idx + 1;
    });

    setOrderedItems(newItems);
    onChange?.(newItems);
  };

  const handleMoveDown = (index: number) => {
    if (index === orderedItems.length - 1) return;

    const newItems = [...orderedItems];
    [newItems[index], newItems[index + 1]] = [newItems[index + 1], newItems[index]];

    newItems.forEach((item, idx) => {
      item.rank = idx + 1;
    });

    setOrderedItems(newItems);
    onChange?.(newItems);
  };

  return (
    <Stack gap="sm">
      <Alert
        color="yellow"
        withCloseButton
        hidden={!isAlertVisible}
        icon={<IconHelpCircle size={16} />}
        onClose={() => setIsAlertVisible(false)}
        title="Not seeing a specific insurance?"
        styles={{ title: { fontSize: "var(--mantine-font-size-md)" } }}
      >
        <TextWrapper>
          Please go to the insurance section on the Billing tab to ensure that it has an address. If
          a subscriber other than "Self" has been selected, an address must be provided for them as
          well.
        </TextWrapper>
      </Alert>
      <Box
        p="md"
        style={{
          borderRadius: "var(--mantine-radius-md)",
          backgroundColor: "var(--mantine-color-gray-1)",
        }}
      >
        <Text mb="md" fw={500} size="sm">
          Please select which insurance
        </Text>
        <Stack gap="sm">
          {orderedItems.map((item, index) => {
            const isFirst = index === 0;
            const isLast = index === orderedItems.length - 1;

            return (
              <Group gap="md" key={item.id} wrap="nowrap" align="center">
                {/* Number */}
                <TextWrapper style={{ minWidth: "20px" }}>{index + 1}.</TextWrapper>

                {/* Up/Down arrows */}
                <Group gap={4}>
                  <ActionIcon
                    size="md"
                    color="gray"
                    variant="default"
                    disabled={isLast}
                    aria-label="Move down"
                    onClick={() => handleMoveDown(index)}
                    style={{
                      border: "1px solid var(--mantine-color-gray-4)",
                      borderRadius: "var(--mantine-radius-sm)",
                    }}
                  >
                    <IconArrowDown size={18} />
                  </ActionIcon>

                  <ActionIcon
                    size="md"
                    color="gray"
                    variant="default"
                    disabled={isFirst}
                    aria-label="Move up"
                    onClick={() => handleMoveUp(index)}
                    style={{
                      border: "1px solid var(--mantine-color-gray-4)",
                      borderRadius: "var(--mantine-radius-sm)",
                    }}
                  >
                    <IconArrowUp size={18} />
                  </ActionIcon>
                </Group>

                <TextWrapper size="sm" style={{ flex: 1 }}>
                  {item.name}
                </TextWrapper>
              </Group>
            );
          })}
        </Stack>
      </Box>
    </Stack>
  );
}
